import imageCompression from 'browser-image-compression';
import { supabase } from './supabase';

const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,           // Max 500KB for compressed version
  maxWidthOrHeight: 1920,   // Max 1920px wide
  useWebWorker: true,
  fileType: 'image/jpeg' as const,
  initialQuality: 0.8,      // 80% JPEG quality
};

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

/**
 * Check if a file is a compressible image
 */
function isCompressibleImage(file: File): boolean {
  return IMAGE_TYPES.includes(file.type) || /\.(jpg|jpeg|png|webp|heic|heif)$/i.test(file.name);
}

/**
 * Compress an image file for in-app display.
 * Returns the original file unchanged if it's not an image or if compression fails.
 */
export async function compressImage(file: File): Promise<File> {
  if (!isCompressibleImage(file)) return file;

  // Skip compression for small files (< 200KB)
  if (file.size < 200 * 1024) return file;

  try {
    const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
    return compressed;
  } catch (error) {
    console.warn('Image compression failed, using original:', error);
    return file;
  }
}

/**
 * Get the compressed path for a given original path.
 * Example: "photos/abc/image.jpg" → "photos/abc/image_compressed.jpg"
 */
function getCompressedPath(originalPath: string): string {
  const lastDot = originalPath.lastIndexOf('.');
  if (lastDot === -1) return `${originalPath}_compressed`;
  return `${originalPath.substring(0, lastDot)}_compressed.jpg`;
}

/**
 * Upload a file with optional compression.
 * - Always uploads the original file at the given path
 * - For images > 200KB, also uploads a compressed version at {path}_compressed.jpg
 * - Returns both URLs
 *
 * Falls back to original-only upload if compression fails.
 */
export async function uploadWithCompression(
  bucket: string,
  path: string,
  file: File,
  options?: { upsert?: boolean }
): Promise<{ originalUrl: string; compressedUrl: string | null; error: Error | null }> {
  const uploadOptions = { upsert: options?.upsert ?? false };

  // Upload original
  const { error: originalError } = await supabase.storage
    .from(bucket)
    .upload(path, file, uploadOptions);

  if (originalError) {
    return { originalUrl: '', compressedUrl: null, error: originalError };
  }

  const { data: originalUrlData } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);

  const originalUrl = originalUrlData.publicUrl;

  // Try to upload compressed version for images
  if (isCompressibleImage(file) && file.size >= 200 * 1024) {
    try {
      const compressed = await compressImage(file);
      const compressedPath = getCompressedPath(path);

      await supabase.storage
        .from(bucket)
        .upload(compressedPath, compressed, uploadOptions);

      const { data: compressedUrlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(compressedPath);

      return { originalUrl, compressedUrl: compressedUrlData.publicUrl, error: null };
    } catch (error) {
      console.warn('Compressed version upload failed, using original only:', error);
      return { originalUrl, compressedUrl: null, error: null };
    }
  }

  return { originalUrl, compressedUrl: null, error: null };
}

/**
 * Helper to upload a file with compression, matching the common pattern in the codebase.
 * Returns the storage path (not URL) for storing in the database.
 *
 * Usage:
 *   const path = `photos/${projectId}/${Date.now()}_${file.name}`;
 *   const { error } = await uploadFileCompressed(bucket, path, file);
 */
export async function uploadFileCompressed(
  bucket: string,
  path: string,
  file: File,
  options?: { upsert?: boolean }
): Promise<{ path: string; error: Error | null }> {
  const { error } = await uploadWithCompression(bucket, path, file, options);
  return { path, error };
}
